// ─── Printing Preview Service ───
// Migrated from Supabase direct INSERT to Alibaba Next Job API.
//
// Worker: gbcospack-printing-preview (PM2) — claims pending/queued jobs,
//         runs artwork pipeline + print_prompts + Kling Omni,
//         stores result in printing-preview-results bucket.
//
// Required: product_id, working_image_id, position_code, print_code, artwork ref
//
// ⛔ Supabase from('printing_preview_jobs').insert(...) 제거
// ⛔ Supabase from('printing_preview_jobs').select(...) 제거 (poll 역시 Job API GET 사용)
// ⛔ Supabase from('printing_preview_results').select(...) 제거 (result는 Job API GET 응답에 포함)
// ⛔ finishing / part-assembly 필드 혼용 금지 (structured_lines, base_image_*, part_image_*, finish_code, part_code)
// ⛔ 클라이언트에서 final_kling_prompt 조립 금지
// ──────────────────────────────────────────────────
import { supabase } from '@/lib/supabase';

// ─── Types (Job API) ───

interface PrintingJobApi {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  progress: {
    step: string | null;
    message: string | null;
    percent: number;
  };
  error: {
    code: string | null;
    message: string | null;
  } | null;
  result: {
    result_id: string | null;
    image_url: string | null;
    storage_bucket: string | null;
    storage_path: string | null;
  } | null;
  client_request_id: string | null;
}

interface CreateJobResponse {
  ok: boolean;
  job: PrintingJobApi;
  request_id: string;
}

interface GetJobResponse {
  ok: boolean;
  job: PrintingJobApi;
  request_id: string;
}

// ─── Service Types ───

export interface PrintingPreviewJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress_percent: number;
  progress_message: string;
  result_id: string | null;
  result_image_url: string | null;
  error_message: string | null;
}

export interface CreatePrintingPreviewRequest {
  product_id: string;
  working_image_id: string;
  position_code: string;
  print_code: string;
  artwork_image_id?: string;
  artwork_image_url?: string;
  source?: string;
  request_locale?: string;
}

// ─── requireApiBaseUrl (absolute only — no relative fallback) ───

function requireApiBaseUrl(): string {
  const base = String(import.meta.env.VITE_API_BASE_URL ?? '')
    .trim()
    .replace(/\/$/, '');
  if (!base) {
    throw new Error(
      'Missing VITE_API_BASE_URL. Printing Job API requires an absolute GBCOSPACK API base URL (no relative /api fallback).',
    );
  }
  if (!/^https?:\/\//i.test(base)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute http(s) URL (got "${base}"). Do not use relative /api paths.`,
    );
  }
  return base;
}

// ─── Map API job → service type ───

function mapApiJobToService(job: PrintingJobApi): PrintingPreviewJob {
  return {
    id: job.id,
    status: job.status,
    progress_percent: job.progress?.percent ?? 0,
    progress_message: job.progress?.message || '',
    result_id: job.result?.result_id || null,
    result_image_url: job.result?.image_url || null,
    error_message: job.error?.message || null,
  };
}

// ─── Create Printing Preview Job (Job API POST) ───

export async function createPrintingPreviewJob(
  request: CreatePrintingPreviewRequest,
): Promise<PrintingPreviewJob> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('로그인이 필요합니다.');
  }

  const apiBase = requireApiBaseUrl();
  const jobId = crypto.randomUUID();

  const body: Record<string, unknown> = {
    id: jobId,
    product_id: request.product_id,
    working_image_id: request.working_image_id,
    position_code: request.position_code,
    print_code: request.print_code,
    source: request.source || 'readdy_customer_ui',
    request_locale: request.request_locale || 'ko-KR',
  };

  // Artwork ref — UUID preferred, URL fallback
  if (request.artwork_image_id) {
    body.artwork_image_id = request.artwork_image_id;
  }
  if (request.artwork_image_url) {
    body.artwork_image_url = request.artwork_image_url;
  }

  console.log('[PrintingPreview] Creating job via Job API:', {
    product_id: request.product_id,
    working_image_id: request.working_image_id,
    position_code: request.position_code,
    print_code: request.print_code,
    has_artwork_id: !!request.artwork_image_id,
    has_artwork_url: !!request.artwork_image_url,
  });

  const res = await fetch(`${apiBase}/api/v1/jobs/printing-preview`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const responseText = await res.text().catch(() => '');
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(responseText);
  } catch {
    console.error('[PrintingPreview] Non-JSON create response:', responseText.slice(0, 500));
    throw new Error(
      `API 서버에서 예상치 못한 응답을 반환했습니다 (HTTP ${res.status}). 잠시 후 다시 시도해주세요.`,
    );
  }

  if (!res.ok || !json.ok) {
    const errMsg = json?.error?.message ?? `Job create failed (${res.status})`;
    console.error('[PrintingPreview] Job API create error:', {
      httpStatus: res.status,
      error: errMsg,
    });
    throw new Error(errMsg);
  }

  console.log('[PrintingPreview] Job created via API:', {
    jobId: json.job.id,
    status: json.job.status,
    requestId: json.request_id,
  });

  return mapApiJobToService(json.job as PrintingJobApi);
}

// ─── Poll Printing Preview Job (Job API GET) ───

export async function pollPrintingJobStatus(jobId: string): Promise<PrintingPreviewJob> {
  const { data: { session } } = await supabase.auth.getSession();
  const apiBase = requireApiBaseUrl();

  const res = await fetch(`${apiBase}/api/v1/jobs/printing-preview/${jobId}`, {
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
    },
  });

  const responseText = await res.text().catch(() => '');
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(responseText);
  } catch {
    console.error('[PrintingPreview] Non-JSON poll response:', responseText.slice(0, 500));
    throw new Error(
      `API 서버에서 예상치 못한 응답을 반환했습니다 (HTTP ${res.status}). 잠시 후 다시 시도해주세요.`,
    );
  }

  if (!res.ok || !json.ok) {
    const errMsg = json?.error?.message ?? `Poll failed (${res.status})`;
    throw new Error(errMsg);
  }

  return mapApiJobToService(json.job as PrintingJobApi);
}